import { supabase } from './supabase';
import type { Company } from '@/types/database';

export interface CreateCompanyData {
  legal_name: string;
  country: string;
  jurisdiction: string | null;
  registration_number: string;
  tax_id: string;
  lei: string | null;
  duns: string | null;
  local_requirements?: Record<string, string> | null;
}

export const companyService = {
  /**
   * Call the `check_company_exists` Postgres RPC (SECURITY DEFINER).
   *
   * Returns the owner/admin email string if a company with this
   * (country, tax_id) pair already exists, or `null` if it is free to create.
   *
   * The RPC is defined as:
   *   create or replace function check_company_exists(p_tax_id text, p_country text)
   *   returns text language plpgsql security definer as $$
   *   declare v_email text;
   *   begin
   *     select u.email into v_email
   *     from   companies c
   *     join   company_members cm on cm.company_id = c.id
   *     join   auth.users      u  on u.id = cm.user_id
   *     where  c.tax_id  = p_tax_id
   *       and  c.country = p_country
   *     order  by (cm.role = 'admin') desc, cm.created_at asc
   *     limit  1;
   *     return v_email;
   *   end;
   *   $$;
   */
  async checkCompanyExists(
    taxId: string,
    country: string
  ): Promise<{ ownerEmail: string | null; error: string | null }> {
    try {
      const { data, error } = await supabase.rpc('check_company_exists', {
        p_tax_id:  taxId.trim(),
        p_country: country.trim(),
      });

      if (error) {
        console.error('[companyService] checkCompanyExists RPC error:', error);
        // If the function simply doesn't exist yet (42883) we fail open
        // so the INSERT itself will hit the DB-level unique constraint instead.
        if ((error as { code?: string }).code === '42883') return { ownerEmail: null, error: null };
        return { ownerEmail: null, error: error.message };
      }

      // data is the scalar return value: string | null
      return { ownerEmail: (data as string | null) ?? null, error: null };
    } catch (err) {
      console.error('[companyService] checkCompanyExists unexpected:', err);
      return { ownerEmail: null, error: null }; // fail open — let DB constraint catch it
    }
  },

  // Check if user belongs to any company
  async getUserCompany(userId: string) {
    const { data, error } = await supabase
      .from('company_members')
      .select(`
        *,
        company:companies(*)
      `)
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle();

    return { data, error };
  },

  /**
   * Create a company and link the user as 'admin' in company_members.
   *
   * RLS note: The companies table INSERT policy requires `created_by = auth.uid()`.
   * The company_members INSERT policy requires the company to already exist
   * AND `user_id = auth.uid()`.  We therefore:
   *   1. Refresh the session before any write so the JWT is fresh.
   *   2. Insert the company with `created_by` explicitly set to the userId.
   *   3. Insert the membership row with `user_id` set to the same userId.
   * If the membership insert fails (e.g. the Supabase service-role is not
   * being used), we attempt a best-effort rollback of the company row.
   */
  async createCompany(userId: string, companyData: CreateCompanyData) {
    try {
      // ── 1. Ensure the auth session / JWT is fresh ──────────────────────────
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session) {
        return {
          data: null,
          error: { message: 'Not authenticated – please log in again.', code: 'NO_SESSION' },
        };
      }

      // ── 2. Insert the company row ──────────────────────────────────────────
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .insert([
          {
            legal_name:          companyData.legal_name,
            country:             companyData.country,
            jurisdiction:        companyData.jurisdiction,
            registration_number: companyData.registration_number,
            tax_id:              companyData.tax_id,
            lei:                 companyData.lei,
            duns:                companyData.duns,
            local_requirements:  companyData.local_requirements ?? null,
            created_by:          userId,  // critical for RLS
            status:              'unverified',
          },
        ])
        .select()
        .single();

      if (companyError) {
        console.error('[companyService] Company INSERT error:', companyError);
        // Surface the real Postgres/RLS message to the caller
        return { data: null, error: companyError };
      }
      if (!company) {
        return { data: null, error: { message: 'Company insert returned no data.' } };
      }

      // ── 3. Read back the membership row created by the DB trigger ─────────
      //
      // The AFTER INSERT trigger `trg_auto_add_company_owner` on `companies`
      // already inserted (company_id, user_id, role='admin') atomically as
      // part of the same transaction.  A second INSERT here would hit the
      // UNIQUE(company_id, user_id) constraint and produce a 409 Conflict.
      // We simply SELECT the row the trigger created.
      const { data: member, error: memberError } = await supabase
        .from('company_members')
        .select()
        .eq('company_id', company.id)
        .eq('user_id', userId)
        .single();

      if (memberError) {
        console.error('[companyService] Member SELECT error:', memberError);
        // Best-effort rollback: delete the orphaned company row
        await supabase.from('companies').delete().eq('id', company.id);
        return { data: null, error: memberError };
      }

      return { data: { company, member }, error: null };
    } catch (err) {
      console.error('[companyService] createCompany unexpected error:', err);
      return {
        data: null,
        error: err instanceof Error ? err : { message: 'Unexpected error during company creation.' },
      };
    }
  },

  // Get all companies for a user — explicitly select active_modules so it
  // is always present even if the wildcard were to be limited by RLS.
  async getUserCompanies(userId: string) {
    const { data, error } = await supabase
      .from('company_members')
      .select(`
        id,
        company_id,
        user_id,
        role,
        module_permissions,
        created_at,
        company:companies(
          id,
          legal_name,
          country,
          jurisdiction,
          registration_number,
          tax_id,
          lei,
          duns,
          local_requirements,
          status,
          created_by,
          created_at,
          invite_token,
          active_modules
        )
      `)
      .eq('user_id', userId);

    return { data, error };
  },

  // Update company legal name
  async updateCompanyName(companyId: string, legalName: string) {
    try {
      const { data, error } = await supabase
        .from('companies')
        .update({ legal_name: legalName })
        .eq('id', companyId)
        .select()
        .single();

      if (error) {
        console.error('Update company name error:', error);
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('updateCompanyName error:', error);
      return { data: null, error };
    }
  },

  /* ─────────────────────────────────────────────────────────────────────────
     HR Join-Link helpers  (permanent invite_token on the companies table)
  ────────────────────────────────────────────────────────────────────────── */

  /**
   * Look up a company by its permanent `invite_token`.
   *
   * ⚠️  RLS problem: the `companies` table is locked to members only.
   *     A fresh employee visiting /join/:token is NOT yet a member, so a direct
   *     `.select()` returns 0 rows (PGRST116 / 406) even though the token is valid.
   *
   * ✅  Fix: call the `get_company_by_invite_token` SECURITY DEFINER RPC.
   *     The function runs as the DB owner (bypasses RLS) but exposes ONLY the
   *     two fields needed (company_id, company_name) – nothing sensitive leaks.
   *
   * SQL to create in Supabase (see PR description for full script):
   *   CREATE OR REPLACE FUNCTION public.get_company_by_invite_token(p_token uuid)
   *   RETURNS TABLE (company_id uuid, company_name text)
   *   LANGUAGE sql SECURITY DEFINER SET search_path = public AS $$
   *     SELECT id, legal_name FROM companies WHERE invite_token = p_token LIMIT 1;
   *   $$;
   */
  async getCompanyByInviteToken(
    inviteToken: string
  ): Promise<{ data: Pick<Company, 'id' | 'legal_name' | 'invite_token'> | null; error: string | null }> {
    try {
      // Cast the token string to uuid on the way in; Supabase passes it as-is.
      const { data, error } = await supabase
        .rpc('get_company_by_invite_token', { p_token: inviteToken });

      if (error) {
        console.error('[companyService] get_company_by_invite_token RPC error:', error);
        return { data: null, error: error.message };
      }

      // The RPC returns an array (RETURNS TABLE); we expect exactly one row.
      const rows = data as { company_id: string; company_name: string }[] | null;
      if (!rows || rows.length === 0) {
        return { data: null, error: 'קישור ההצטרפות אינו תקין.' };
      }

      const row = rows[0]!;
      return {
        data: {
          id:           row.company_id,
          legal_name:   row.company_name,
          invite_token: inviteToken,   // echo back – caller already knows this
        } as Pick<Company, 'id' | 'legal_name' | 'invite_token'>,
        error: null,
      };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  },

  /**
   * Fetch just the invite_token for a given company ID (used by the Manager UI).
   */
  async getInviteToken(
    companyId: string
  ): Promise<{ data: string | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('invite_token')
        .eq('id', companyId)
        .single();

      if (error) {
        console.error('[companyService] getInviteToken error:', error);
        return { data: null, error: error.message };
      }

      return { data: (data as { invite_token: string | null }).invite_token, error: null };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  },
};
